import { useState } from 'react'
import { useRouter } from 'next/router'

import DeckHelpService from 'services/deckHelp.service'
import ToastService from 'services/toast.service'

import { emptyTextArea } from 'types/editor'
import { SET_ACTIVE_STATE } from 'app-redux/active/actions/types'
import { SET_DECK_STATE } from 'app-redux/deck/actions/types'

import { usePinnedInteractions } from 'hooks/usePinnedSidecar'
import { useActionless, useAppSelector } from 'app-redux/hooks'
import useDeckActions from 'components/deckPage/actions/menuActions'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import PhatButton from 'components/formElements/PhatButton'
import Editor from 'components/formElements/richText/Editor'
import DeckTags from 'components/deckPage/header/DeckHeader/DeckTags'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import Link from 'components/elements/Link'
import DeckTypeahead from 'components/misc/DeckTypeahead'

import styles from './deckHelpOverlay.module.scss'

import { generateDeckUrl } from 'utils/deckSeo'

const LOWER_LIMIT = 30
const UPPER_LIMIT = 200

const generateGoalCountWarning = (wordCount: number) => {
  if (wordCount > LOWER_LIMIT && wordCount < UPPER_LIMIT) return ''

  return `Your Goals section could be a bit ${wordCount > UPPER_LIMIT ? 'shorter' : 'longer'}. Try to provide clear and succinct context for what your deck is aiming to do.`
}

const generateIssuesWordCountWarning = (wordCount: number) => {
  if (wordCount > LOWER_LIMIT && wordCount < UPPER_LIMIT) return ''

  return `Your Requests section could be a bit ${wordCount > UPPER_LIMIT ? 'shorter' : 'longer'}. Try to provide a clear and succinct explanation of what sort of help you’re looking for. If you’re unsure of specific issues, try using our playtester a few times to fishbowl the deck and see where it comes up short.`
}

const DeckHelpOverlay = () => {
  const router = useRouter()

  const deckActions = useDeckActions()

  const open = useAppSelector(state => state.active.modals.deckHelp)

  const deckHelpId = useAppSelector(state => state.deck.deckHelp?.id)
  const deckGoals = useAppSelector(state => state.deck.deckHelp?.goals)
  const deckIssues = useAppSelector(state => state.deck.deckHelp?.issues)
  const deckHelpCreatedAt = useAppSelector(state => state.deck.deckHelp?.createdAt)

  const reduxDeckId = useAppSelector(state => state.deck.id)
  const reduxDeckTags = useAppSelector(state => state.deck.deckTags)

  const [deckId, setDeckId] = useState(reduxDeckId || null)
  const [deckTags, setDeckTags] = useState(reduxDeckTags || [])

  const [setDeckState, setActiveState] = useActionless(SET_DECK_STATE, SET_ACTIVE_STATE)

  const [pinnedInteractionStatus, setPinnedInteractionStatus] = usePinnedInteractions()

  const [goals, setGoals] = useState(deckGoals || emptyTextArea)
  const [issues, setIssues] = useState(deckIssues || emptyTextArea)
  const [removeDeckHelpPostOpen, setRemoveDeckHelpPostOpen] = useState(false)

  const [goalWordCount, setGoalWordCount] = useState(0)
  const [issuesWordCount, setIssuesWordCount] = useState(0)

  const [loading, setLoading] = useState(false)

  const handleCreateOrUpdateDeckHelpPost = () => {
    if (!deckId)
      return ToastService.create('Cannot create deck help request for missing Deck ID', 'Deck help service', 'error')

    if (goalWordCount < 2) return ToastService.create('"Goals" section is too short. Try adding a bit more context.')
    if (issuesWordCount < 2) return ToastService.create('"Issues" section is too short. Try adding a bit more context.')

    setLoading(true)

    if (deckHelpId)
      return DeckHelpService.update(deckHelpId, { goals, issues, active: true })
        .then(deckHelp => {
          ToastService.create('Updated deck help request', 'Deck help service', 'success')

          setDeckState({ deckHelp, loadedWithDeckHelp: true })
        })
        .catch(err => ToastService.create('Unable to update deck help', 'Deck help service', 'error'))
        .finally(() => setLoading(false))

    return DeckHelpService.create(deckId, { active: true, goals, issues })
      .then(deckHelp => {
        ToastService.create('Created deck help request', 'Deck help service', 'success')

        if (!reduxDeckId) return router.push(generateDeckUrl(deckId))

        setDeckState({ deckHelp, loadedWithDeckHelp: true })
        setPinnedInteractionStatus({ locked: true, type: 'deckHelp', open: false })
        handleClose()
      })
      .catch(err => ToastService.create(err, 'Deck help service', 'error'))
      .finally(() => setLoading(false))
  }

  const handleRemoveDeckHelpPost = () => {
    if (!deckHelpId) return Promise.resolve()

    setLoading(true)

    return DeckHelpService.update(deckHelpId, { active: false, goals, issues })
      .then(() => {
        setDeckState({ deckHelp: null, loadedWithDeckHelp: false })

        // If the pinned interaction panel is open && on the deckHelp tab, close it
        if (
          (pinnedInteractionStatus.locked || pinnedInteractionStatus.open) &&
          pinnedInteractionStatus.type === 'deckHelp'
        )
          setPinnedInteractionStatus({ locked: false, open: false, type: 'description' })

        handleClose()
      })
      .catch(err => ToastService.create(err, 'Deck help service', 'error'))
      .finally(() => setLoading(false))
  }

  const handleClose = () => setActiveState({ modals: {} })

  const handleGetWordCount = (text: string) => {
    const trimmedText = text?.trim() || ''

    if (!trimmedText) return 0

    return trimmedText.split(/\s+/).length
  }

  const goalWarning = generateGoalCountWarning(goalWordCount)
  const issuesWarning = generateIssuesWordCountWarning(issuesWordCount)
  const showWarning = !reduxDeckTags.length || goalWarning || issuesWarning
  const disableSave = !deckId || loading

  const expiresAt = deckHelpCreatedAt ? new Date(deckHelpCreatedAt) : new Date()
  expiresAt.setDate(expiresAt.getDate() + 7) // Add seven days

  return (
    <>
      <GlobalOverlayStack
        open={open}
        onClose={handleClose}
        className={styles.container}
        mobileToolbarOptions={[
          {
            label: 'Delist',
            icon: 'trash alternate outline',
            hidden: !deckHelpId,
            disabled: !deckId || loading,
            onClick: () => setRemoveDeckHelpPostOpen(true),
          },
          {
            label: deckHelpId ? 'Update' : 'Create',
            icon: 'save outline',
            disabled: disableSave,
            onClick: handleCreateOrUpdateDeckHelpPost,
          },
        ]}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h2>Deck help</h2>
            {deckHelpId && <div>Expires at: {expiresAt.toLocaleDateString()}</div>}
          </div>

          {/* Don't show this while not on the deck page */}
          {!!reduxDeckId && (
            <div className={styles.message}>
              <p>
                Looking for suggestions of what to add and/or cut from your deck? Use this menu to add your deck to the
                <Link href="/deck-help"> Deck help requests</Link> link under <b>Decks</b> on the site’s global
                navigation bar. Deck help sessions are open for seven days, but can be reopened after time expires.
                Comments from other users remain after sessions expire.
              </p>

              <p>
                Using the fields below, provide descriptions of what your deck is trying to do and what you’re currently
                struggling with. Clear and succinct explanations will help other users provide more meaningful feedback.
              </p>
            </div>
          )}

          {!reduxDeckId && (
            <div>
              <h3>Select deck</h3>
              <DeckTypeahead
                placeholder="Super awesome deck name"
                onDeckSelected={(id, tags) => {
                  setDeckId(id)
                  setDeckTags(tags)
                }}
              />
            </div>
          )}

          <div>
            <h3>Deck tags</h3>
            <DeckTags deckTags={deckTags} />
          </div>

          <Editor
            disable={!deckId}
            value={goals}
            onChange={setGoals}
            header="Goals: What is this deck trying to do?"
            placeholder="This deck is trying to win via attackers..."
            onTextChange={output => setGoalWordCount(handleGetWordCount(output))}
          />
          <div className={styles.wordCount}>Word count: {goalWordCount}</div>

          <Editor
            disable={!deckId}
            value={issues}
            onChange={setIssues}
            header="Requests: What does this deck need help with?"
            placeholder="I never feel like I can keep enough power on-board to be threatening..."
            onTextChange={output => setIssuesWordCount(handleGetWordCount(output))}
          />
          <div className={styles.wordCount}>Word count: {issuesWordCount}</div>

          {showWarning && (
            <div className={styles.warning}>
              <h3>Warning! Consider the following to improve your chance of getting quality responses:</h3>
              <ul>
                {!deckTags.length && (
                  <li>
                    We highly recommend setting{' '}
                    <button disabled={!reduxDeckId} onClick={deckActions.deckTags.onClick}>
                      deck tags
                    </button>{' '}
                    if you want to request help from others.
                  </li>
                )}

                {goalWarning && <li>{goalWarning}</li>}
                {issuesWarning && <li>{issuesWarning}</li>}
              </ul>
            </div>
          )}

          <div className={styles.controls}>
            {deckHelpId && (
              <PhatButton color="red" onClick={() => setRemoveDeckHelpPostOpen(true)} disabled={loading}>
                Remove deck help request
              </PhatButton>
            )}
            <PhatButton onClick={handleCreateOrUpdateDeckHelpPost} color="green" disabled={disableSave}>
              {deckHelpId ? 'Update deck help request' : 'Create deck help request'}
            </PhatButton>
          </div>
        </div>
      </GlobalOverlayStack>

      <ConfirmDeleteModal
        open={removeDeckHelpPostOpen}
        onClose={() => setRemoveDeckHelpPostOpen(false)}
        onDelete={handleRemoveDeckHelpPost}
        header="Delist deck help request"
        message="Are you sure you want to delist your deck help request? Doing so will remove it from the help request board."
        buttonText="Delist post"
      />
    </>
  )
}

export default DeckHelpOverlay
